import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useGuest } from '../context/GuestContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { FlipValueLogoBox } from '../components/FlipValueLogo';
import { Zap, ArrowLeft, Moon, Sun, Eye, EyeOff, Loader2, Sparkles, Check, Mail } from 'lucide-react';

// Google icon SVG
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Apple icon SVG
const AppleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  
  const [isLogin, setIsLogin] = useState(mode === 'signin');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  
  const emailInputRef = useRef(null);
  const navigate = useNavigate();
  const { login, signup, signInWithGoogle, signInWithApple, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { totalActions, lifetimeLimit } = useGuest();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Update mode when URL changes
  useEffect(() => {
    setIsLogin(mode === 'signin');
    setShowEmailForm(false);
  }, [mode]);

  // Auto-focus email input when showing email form
  useEffect(() => {
    if (showEmailForm && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [showEmailForm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!', {
          icon: <Sparkles className="w-4 h-4 text-primary" />
        });
        navigate('/dashboard');
      } else {
        // Signup flow
        if (!formData.name.trim()) {
          toast.error('Please enter your name');
          setLoading(false);
          return;
        }
        const result = await signup(formData.email, formData.password, formData.name);
        
        // Check if email confirmation is required
        if (result?.user && !result.session) {
          // Email confirmation required
          setShowVerificationMessage(true);
          toast.success('Check your email to verify your account!', {
            icon: <Mail className="w-4 h-4 text-primary" />,
            duration: 5000
          });
        } else {
          // No email confirmation required - go to dashboard
          toast.success('Account created! Welcome to FlipValue!', {
            icon: <Sparkles className="w-4 h-4 text-primary" />
          });
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      // Handle body stream error - likely means auth succeeded but parsing failed
      if (error.message && error.message.includes('body stream already read')) {
        // Check if user is actually logged in
        const { data } = await supabase.auth.getSession();
        if (data?.session) {
          toast.success('Welcome!', {
            icon: <Sparkles className="w-4 h-4 text-primary" />
          });
          navigate('/dashboard');
          return;
        }
      }
      const message = error.message || 'Something went wrong';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      toast.error('Google sign-in failed. Please try again.');
    }
  };

  const handleAppleLogin = () => {
    toast.info('Apple Sign-In coming soon!');
  };

  const switchMode = (newMode) => {
    navigate(`/auth?mode=${newMode}`, { replace: true });
  };

  const benefits = [
    'Unlimited searches & valuations',
    'Unlimited inventory tracking',
    'Price alerts & notifications',
    'Sync across all devices',
    'Export to CSV'
  ];

  if (oauthLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="w-full px-4 py-3 flex items-center justify-between border-b border-border">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-secondary rounded-full transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        
        <button
          onClick={toggleTheme}
          className="p-2 hover:bg-secondary rounded-full transition-colors"
        >
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-4 py-8">
        <div className="w-full max-w-sm space-y-6">
          {/* Email Verification Message */}
          {showVerificationMessage ? (
            <div className="text-center space-y-6">
              <div className="mx-auto mb-4 flex justify-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
              </div>
              <h1 className="text-2xl font-bold">Check Your Email</h1>
              <p className="text-muted-foreground">
                We sent a verification link to<br />
                <span className="font-medium text-foreground">{formData.email}</span>
              </p>
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 text-left">
                <p className="text-sm text-muted-foreground">
                  Click the link in the email to verify your account, then come back and sign in.
                </p>
              </div>
              <div className="space-y-3 pt-4">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setShowVerificationMessage(false);
                    setIsLogin(true);
                    setShowEmailForm(true);
                  }}
                >
                  Back to Sign In
                </Button>
                <p className="text-xs text-muted-foreground">
                  Didn't receive the email? Check your spam folder.
                </p>
              </div>
            </div>
          ) : (
          <>
          {/* Logo & Title */}
          <div className="text-center mb-8">
            <div className="mx-auto mb-4 flex justify-center">
              <FlipValueLogoBox size="xl" withGlow={true} />
            </div>
            <h1 className="text-2xl font-bold">
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {isLogin 
                ? 'Sign in to access your saved data' 
                : `You've used ${totalActions}/${lifetimeLimit} free searches`}
            </p>
          </div>

          {/* Benefits - only on signup */}
          {!isLogin && !showEmailForm && (
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mb-6">
              <p className="text-sm font-medium mb-3 text-primary">Why create an account?</p>
              <ul className="space-y-2">
                {benefits.map((benefit, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Auth Buttons */}
          {!showEmailForm ? (
            <div className="space-y-3">
              {/* Google */}
              <Button
                variant="outline"
                className="w-full h-12 gap-3 text-base font-medium border-2 hover:bg-secondary/50"
                onClick={handleGoogleLogin}
              >
                <GoogleIcon />
                Continue with Google
              </Button>

              {/* Apple */}
              <Button
                variant="outline"
                className="w-full h-12 gap-3 text-base font-medium border-2 hover:bg-secondary/50"
                onClick={handleAppleLogin}
              >
                <AppleIcon />
                Continue with Apple
              </Button>

              {/* Divider */}
              <div className="relative py-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">or</span>
                </div>
              </div>

              {/* Email option - with border box */}
              <Button
                variant="outline"
                className="w-full h-12 gap-3 text-base font-medium border-2 hover:bg-secondary/50"
                onClick={() => setShowEmailForm(true)}
              >
                <Mail className="w-5 h-5" />
                {isLogin ? 'Sign in with Email' : 'Sign up with Email'}
              </Button>
            </div>
          ) : (
            /* Email Form */
            <form onSubmit={handleSubmit} className="space-y-4">
              <button
                type="button"
                onClick={() => setShowEmailForm(false)}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to options
              </button>

              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="h-12"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  ref={emailInputRef}
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="h-12"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    className="h-12 pr-10"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {isLogin && (
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className="text-sm text-primary hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : isLogin ? (
                  'Sign In'
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>
          )}

          {/* Toggle login/signup */}
          <div className="text-center pt-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              {isLogin ? "Don't have an account?" : 'Already have an account?'}
              <button
                type="button"
                onClick={() => switchMode(isLogin ? 'signup' : 'signin')}
                className="ml-1 text-primary font-medium hover:underline"
              >
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>

          {/* Skip option */}
          <div className="text-center space-y-3">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Continue as guest ({lifetimeLimit - totalActions} searches remaining)
            </button>
          </div>
          </>
          )}
        </div>
      </main>
    </div>
  );
}
